import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, DollarSign, Clock, ChevronDown, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function MyEarnings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [expandedWeek, setExpandedWeek] = useState(null);

  useEffect(() => {
    fetchEarnings();
  }, [year]);

  const fetchEarnings = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/time/my-earnings?year=${year}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setData(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch earnings:', e);
    }
    setLoading(false);
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 3; y--) {
    yearOptions.push(y);
  }

  if (loading) {
    return <div className="text-center py-6 text-gray-500">Loading earnings...</div>;
  }

  if (!data) {
    return <div className="text-center py-6 text-gray-500">Unable to load earnings data</div>;
  }

  return (
    <div data-testid="my-earnings">
      {/* Year selector + Summary */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-600" />
          My Earnings
        </h3>
        <Select value={String(year)} onValueChange={(val) => setYear(parseInt(val))}>
          <SelectTrigger className="w-28" data-testid="earnings-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {yearOptions.map(y => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* YTD Summary Cards */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-green-50 rounded-lg p-3 text-center border border-green-200">
          <DollarSign className="w-4 h-4 mx-auto text-green-600 mb-1" />
          <p className="text-lg font-bold text-green-700">${data.year_total_earnings.toLocaleString()}</p>
          <p className="text-xs text-green-600">Year Total</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-200">
          <Clock className="w-4 h-4 mx-auto text-blue-600 mb-1" />
          <p className="text-lg font-bold text-blue-700">{data.year_total_hours}</p>
          <p className="text-xs text-blue-600">Total Hours</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-3 text-center border border-purple-200">
          <TrendingUp className="w-4 h-4 mx-auto text-purple-600 mb-1" />
          <p className="text-lg font-bold text-purple-700">${data.hourly_rate}</p>
          <p className="text-xs text-purple-600">Hourly Rate</p>
        </div>
      </div>

      {/* Weekly Breakdown */}
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-3 py-2 border-b">
          <p className="text-sm font-semibold text-gray-700">Weekly Breakdown <span className="text-xs font-normal text-gray-500">(tap a week to see each day)</span></p>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {data.weeks.length === 0 ? (
            <p className="text-center text-gray-400 py-6 text-sm">No earnings data for {year}</p>
          ) : (
            data.weeks.map((week, idx) => {
              const isOpen = expandedWeek === week.week;
              const hasDays = Array.isArray(week.days) && week.days.length > 0;
              return (
                <div key={idx} className="border-b last:border-b-0">
                  <button
                    type="button"
                    onClick={() => setExpandedWeek(isOpen ? null : week.week)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition`}
                    data-testid={`earnings-week-row-${week.week}`}
                    aria-expanded={isOpen}
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                              : <ChevronRight className="w-3.5 h-3.5 text-gray-500 shrink-0" />}
                      <div className="min-w-0">
                        <p className="font-medium text-gray-800">Week {week.week}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(week.week_start + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(week.week_end + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-semibold text-green-700">${week.earnings.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">
                        {week.total_hours} hrs
                        {week.adjustment_hours !== 0 && (
                          <span className={week.adjustment_hours > 0 ? 'text-green-600' : 'text-red-600'}>
                            {' '}({week.adjustment_hours > 0 ? '+' : ''}{week.adjustment_hours})
                          </span>
                        )}
                      </p>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="bg-blue-50/50 px-3 py-2 border-t" data-testid={`earnings-week-days-${week.week}`}>
                      {!hasDays ? (
                        <p className="text-xs text-gray-400 py-2">No daily entries.</p>
                      ) : (
                        <ul className="divide-y divide-blue-100">
                          {week.days.map((day) => (
                            <li key={day.date} className="flex items-center justify-between py-1.5 text-xs">
                              <div className="min-w-0">
                                <p className="font-medium text-gray-700">{day.day_name}</p>
                                <p className="text-[11px] text-gray-500">
                                  {new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  {day.entries && day.entries.length > 0 && (
                                    <> · {day.entries.length} shift{day.entries.length === 1 ? '' : 's'}</>
                                  )}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className={`font-semibold ${day.hours > 0 ? 'text-green-700' : 'text-gray-400'}`}>
                                  {day.hours} hrs
                                </p>
                                <p className="text-[11px] text-gray-500">
                                  ${day.earnings.toLocaleString()}
                                  {day.adjustment_hours !== 0 && (
                                    <span className={day.adjustment_hours > 0 ? 'text-blue-600 ml-1' : 'text-red-600 ml-1'}>
                                      ({day.adjustment_hours > 0 ? '+' : ''}{day.adjustment_hours} adj)
                                    </span>
                                  )}
                                </p>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default MyEarnings;
